import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { AuthenticationManager } from "../../authentication";
import { LocalizerService, NavigationService } from "../../shared";
import { UserAttributes } from "../../shared/models/user-attributes";
import { DialogsService } from "../../dialogs/services/dialogs.service";
import { ProfileSettingsComponent } from "../../account/profile-settings/profile-settings.component";
import { UnitsManager } from "../../units/services/units-manager";
import { InstallationsManager } from "../../installations/services/installations-manager";
import { Unit } from "../../shared/models/units/unit";
import { Installation } from "../../shared/models/installations/installation";
import { Pbx } from "../../pbx/models/pbx";
import { PbxMode } from "../../pbx/models/pbx_mode";
import {catchError, finalize, forkJoin, Observable, of, switchMap} from "rxjs";
import {PbxManager} from "../../pbx/services/pbx-manager";
import {NotificationsManagerService} from "../../notifications/services/notifications-manager.service";
import {PageTitleService} from "../../shared/services/page-title.service";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrl: "./header.component.scss",
})
export class HeaderComponent implements OnInit {
  private _sidebarOpened: boolean = false;

  @Output()
  sidebarButtonClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

  sidebarButtonIcon = "menu";

  userAttributes: UserAttributes | null = null;

  menuItems: any[] = [];

  pbxs: Pbx[] = [];
  changingModeMap: Record<string, boolean> = {};
  pageTitle$: Observable<string | null>;
  loggingOut = false;

  protected readonly PbxMode = PbxMode;


  constructor(
    private auth: AuthenticationManager,
    private navigation: NavigationService,
    private dialogs: DialogsService,
    private localizer: LocalizerService,
    private unitsManager: UnitsManager,
    private installationsManager: InstallationsManager,
    private pbxManager: PbxManager,
    private notificationsManager: NotificationsManagerService,
    pageTitleService: PageTitleService
  ) {
    this.pageTitle$ = pageTitleService.title$;
  }

  ngOnInit(): void {
    this.auth.currentUserAttributes().subscribe({
      next: (attributes) => (this.userAttributes = attributes),
    });

    forkJoin({
      units: this.unitsManager.getUnits().pipe(catchError(() => of([]))),
      installations: this.installationsManager.getInstallations().pipe(catchError(() => of([]))),
      pbxs: this.pbxManager.getPbxs().pipe(catchError(() => of([])))
    }).subscribe(({ units, installations, pbxs }) => {
      this.pbxs = pbxs;

      this.addMenuItemGroup<Pbx>({
        items: pbxs,
        labelKey: "General.Pbxs",
        iconPath: "assets/icons/ic_door.svg",
        getText: (p) => p.name,
        getAction: (p) => this.openPbxDetail.bind(this, p),
      });

      this.addMenuItemGroup<Unit>({
        items: units,
        labelKey: "General.Unit",
        iconPath: "assets/icons/ic_home.svg",
        getText: (u) => u.name!,
        getAction: (u) => this.openUnitDetail.bind(this, u),
      });

      this.addMenuItemGroup<Installation>({
        items: installations,
        labelKey: "General.Installation",
        iconPath: "assets/icons/ic_buildings.svg",
        getText: (i) => i.name,
        getAction: (i) => this.openInstallationDetail.bind(this, i),
      });

      this.menuItems.push(
        { text: this.localizer.getLocalizedString("Sidebar.ProfileSettings"), action: this.showProfileSettings.bind(this) },
        { text: this.localizer.getLocalizedString("Auth.Logout"), cssClass: 'logout-action', action: this.logout.bind(this) }
      );
    });
  }

  toggleSidebar() {
    this._sidebarOpened = !this._sidebarOpened;
    this.sidebarButtonClicked.emit(this._sidebarOpened);
    this.sidebarButtonIcon = this._sidebarOpened ? "close" : "menu";
  }

  onContextItemClicked(event: any) {
    if (!event || !event.hasOwnProperty("itemData") || !event.itemData.hasOwnProperty("action")) {
      console.error("Cannot handle context menu item click event", event);
      return;
    }
    event.itemData.action();
  }

  getPbxModeIcon(pbx: Pbx): string {
    return pbx.pbx_mode === PbxMode.ALWAYS_PRESENT
      ? 'assets/icons/ic_alarm.svg'
      : 'assets/icons/ic_moon.svg';
  }

  togglePbxMode(pbx: Pbx) {
    if (this.changingModeMap[pbx.id]) {
      return;
    }

    this.changingModeMap[pbx.id] = true;
    const newMode = pbx.pbx_mode === PbxMode.ALWAYS_PRESENT ? PbxMode.ALWAYS_ABSENT : PbxMode.ALWAYS_PRESENT;
    this.pbxManager.changePbxMode(pbx.installation_id, pbx.id, newMode)
      .pipe(finalize(() => this.changingModeMap[pbx.id] = false))
      .subscribe({
        next: (updated) => {
          pbx.pbx_mode = updated.pbx_mode;
        },
        error: (err) => {
          console.error('Error changing PBX mode', err);
        }
      });
  }

  private showProfileSettings() {
    const configuration = {
      title: "Account.EditProfile",
      icon: "assets/icons/ic_edit.svg",
    };

    const data = { userAttributes: this.userAttributes };
    this.dialogs.showDialog(ProfileSettingsComponent, data, configuration);
  }

  private logout() {
    this.loggingOut = true;
    this.notificationsManager.unsubscribeFromNotifications().pipe(
        switchMap(() => this.auth.logout())
    ).subscribe({
      next: () => this.navigation.navigateToLogin(),
      error: () => {
        this.loggingOut = false;
      }
    });
  }

  private addMenuItemGroup<T>(options: MenuItemGroupOptions<T>) {
    if (!options.items?.length) {
      return;
    }

    const groupHeader = {
      text: this.localizer.getLocalizedString(options.labelKey),
      icon: options.iconPath,
      cssClass: "actions-group-title",
    };

    const items = options.items.map((item) => ({
      text: options.getText(item),
      action: options.getAction(item),
    })) as any[];

    if (items.length > 0) {
      items[items.length - 1].cssClass = "group-action-divider";
    }

    this.menuItems.push(groupHeader, ...items);
  }

  private openUnitDetail(unit: Unit) {
    this.navigation.navigateToUnitDashboard(unit.installation_id, unit.id);
  }

  private openInstallationDetail(installation: Installation) {
    this.navigation.navigateToInstallationDetail(installation.id);
  }

  private openPbxDetail(pbx: Pbx) {
    this.navigation.navigateToPbxDashboard(pbx.installation_id, pbx.id);
  }
}

interface MenuItemGroupOptions<T> {
    items: T[];
    labelKey: string;
    iconPath: string;
    getText: (item: T) => string;
    getAction: (item: T) => () => void;
}
